import { browserLogger } from '@newfront-insurance/dd-rum';

import { NOVA_NOTIFICATION_ICON } from '../lib/notification-icon';

const STORAGE_KEY = 'nova-chat-notifications-enabled';

export interface BrowserNotificationManager {
  // Permission management
  requestPermission(): Promise<NotificationPermission>;
  hasPermission(): boolean;

  // Browser support check
  isSupported(): boolean;
  getPermission(): NotificationPermission;

  // Notification display
  showChatNotification(chatTitle: string, chatUuid: string, forceShow?: boolean): void;
  clearNotifications(): void;

  // Settings
  isEnabled(): boolean;
  enable(): void;
  disable(): void;

  // Feature flag integration
  setFeatureFlagEnabled(enabled: boolean): void;

  // Status
  getActiveNotificationCount(): number;
}

class BrowserNotificationManagerImpl implements BrowserNotificationManager {
  private notificationRefs: Notification[] = [];

  private featureFlagEnabled: boolean = true;

  /**
   * Check if browser supports notifications
   */
  isSupported(): boolean {
    return 'Notification' in window;
  }

  /**
   * Retrieve current notification permission status
   */
  getPermission(): NotificationPermission {
    if (!this.isSupported()) {
      return 'denied';
    }

    return Notification.permission;
  }

  /**
   * Request browser notification permission from the user
   */
  async requestPermission(): Promise<NotificationPermission> {
    const currentPermission = this.getPermission();
    if (currentPermission !== 'default') {
      // Permission already granted or denied
      return currentPermission;
    }

    try {
      const permission = await Notification.requestPermission();
      browserLogger.info('[Nova Notifications] Permission requested', { permission });
      return permission;
    } catch (error) {
      browserLogger.error('[Nova Notifications] Error requesting permission', { error });
      return 'denied';
    }
  }

  /**
   * Check if we have notification permission
   */
  hasPermission(): boolean {
    return this.getPermission() === 'granted';
  }

  /**
   * Display a notification for a completed chat
   */
  showChatNotification(chatTitle: string, chatUuid: string): void {
    if (!this.isEnabled()) {
      browserLogger.debug('[Nova Notifications] Notifications disabled, skipping notification');
      return;
    }

    if (!this.hasPermission()) {
      browserLogger.warn('[Nova Notifications] No permission to show notifications');
      return;
    }

    try {
      const notification = new Notification(`Nova: New messages available`, {
        body: chatTitle,
        icon: NOVA_NOTIFICATION_ICON,
        tag: `chat-${chatUuid}`, // Prevent duplicate notifications for same chat
        badge: NOVA_NOTIFICATION_ICON,
        requireInteraction: false,
      });

      notification.onclick = () => {
        // Focus the app and bring it to the foreground
        window.focus();

        // Dispatch a custom event that can be handled by the chat overlay to open the specific chat
        window.dispatchEvent(
          new CustomEvent('nova-chat-notification-click', {
            detail: { chatUuid, chatTitle },
          }),
        );

        notification.close();
      };

      this.notificationRefs.push(notification);

      browserLogger.debug('[Nova Notifications] Browser notification displayed', {
        chatTitle,
        chatUuid,
      });
    } catch (error) {
      browserLogger.error('[Nova Notifications] Failed to display notification', { error, chatTitle, chatUuid });
    }
  }

  /**
   * Clear all active notifications
   */
  clearNotifications(): void {
    this.notificationRefs.forEach((notification) => {
      notification.close();
    });
    this.notificationRefs = [];
    browserLogger.debug('[Nova Notifications] Cleared all notifications');
  }

  /**
   * Check if notifications are enabled by feature flag and user preference
   * For testing: always enabled by default
   */
  isEnabled(): boolean {
    // First check if the feature flag is enabled
    if (!this.featureFlagEnabled) {
      return false;
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const result = stored === 'true';
      return result;
    } catch (error) {
      return false;
    }
  }

  /**
   * Enable notifications
   */
  enable(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(true));
    browserLogger.debug('[Nova Notifications] Notifications enabled');
  }

  /**
   * Disable notifications
   */
  disable(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(false));
    this.clearNotifications();
    browserLogger.debug('[Nova Notifications] Notifications disabled');
  }

  /**
   * Set whether the nova-notifications feature flag is enabled
   */
  setFeatureFlagEnabled(enabled: boolean): void {
    this.featureFlagEnabled = enabled;
    browserLogger.debug('[Nova Notifications] Feature flag updated', { enabled });
  }

  /**
   * Get the number of currently active notifications
   */
  getActiveNotificationCount(): number {
    return this.notificationRefs.length;
  }
}

// Singleton instance
export const browserNotificationManager = new BrowserNotificationManagerImpl();
